import { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import * as benefitApis from 'apis/benefits';
import './OfferDetailsStatic.scss';

/*
  Offer Details layout, converted from the offer-details.html mockup.
  Pluggable, self-contained component:
  - All CSS is scoped under ".od-page" with "od-" prefixed classes,
    so it can be dropped into the public site or the Dashboard
    without colliding with existing global styles.
  - Renders real benefit/partner data from the API. Empty fields/sections
    are omitted (no static content fallbacks).

  Field mapping back to taurian-backend (Benefit + Partner entities):
    od-category-badge -> benefit.category
    od-title-block h2  -> benefit.name
    od-offer-box       -> benefit.shortDescription (offer summary) +
                          benefit.offerCode (copyable code), or
                          benefit.offerText when benefit.isOffline is set
                          (store-only benefit: no code to copy; the partner
                          website row and Visit Website button still show)
    od-contact-row (website) -> benefit.partner.website
    od-contact-row (address) -> benefit.partner.addresses[]
    od-contact-row (phone)   -> benefit.partner.phone
    od-about-panel p         -> benefit.description (full write-up - the
                                 short teaser on the listing page uses the
                                 separate benefit.shortDescription field)
    od-ad-banner             -> benefit.partner.ads[]

  Note: the Partner entity has no "email" column, so there is no email
  contact row here (the old OfferDetails.js referenced one, but it was
  never actually backed by data).
*/

const OfferDetailsStatic = () => {
  const { id } = useParams();
  const location = useLocation();
  const assetUrl = process.env.REACT_APP_TAURIAN_ASSET_URL;

  const [benefit, setBenefit] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return;
    setBenefit(null);
    setActiveImage(0);

    const fetchData = async () => {
      try {
        const response = await benefitApis.fetchBenefitWithPartner(id);
        setBenefit(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, [id]);

  const partner = benefit?.partner;

  const galleryImages = (benefit?.images?.length ? benefit.images : partner?.images?.length ? partner.images : [])
    .map((img) => `${assetUrl}${img}`);

  const addresses = partner?.addresses?.length ? partner.addresses : [];
  const ads = partner?.ads?.length ? partner.ads.map((ad) => `${assetUrl}${ad}`) : [];

  // Store-only benefits are redeemed in person at the outlet: they carry a
  // plain offerText instead of a coupon code. The partner website is still
  // shown when one is set.
  const isOffline = Boolean(benefit?.isOffline);
  const showWebsite = Boolean(partner?.website);

  const hasOfferBox = Boolean(
    benefit?.shortDescription || (isOffline ? benefit?.offerText : benefit?.offerCode),
  );
  const hasContactInfo = Boolean(showWebsite || addresses.length || partner?.phone);

  const isDashboard = location.pathname.startsWith('/dashboard');
  const backHref = isDashboard ? '/dashboard' : '/Offers';

  const handleCopyCode = () => {
    if (!benefit?.offerCode) return;
    navigator.clipboard.writeText(benefit.offerCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const showThumbnail = (index) => {
    const total = galleryImages.length;
    setActiveImage(((index % total) + total) % total);
  };

  return (
    <div className="od-page">
      <div className="od-container">

        <Link to={backHref} className="od-back-link"><i className="fa-solid fa-arrow-left"></i> Back to Offers</Link>

        {/* ===== HERO: GALLERY + INFO ===== */}
        <div className="od-hero-wrap">

          {galleryImages.length > 0 && (
            <div className="od-gallery">
              <div className="od-main-image">
                <img src={galleryImages[activeImage]} alt={benefit?.name || ''} />
                {galleryImages.length > 1 && (
                  <>
                    <div className="od-gallery-nav od-prev" onClick={() => showThumbnail(activeImage - 1)}>
                      <i className="fa-solid fa-chevron-left"></i>
                    </div>
                    <div className="od-gallery-nav od-next" onClick={() => showThumbnail(activeImage + 1)}>
                      <i className="fa-solid fa-chevron-right"></i>
                    </div>
                  </>
                )}
              </div>

              {galleryImages.length > 1 && (
                <div className="od-thumbnail-row">
                  {galleryImages.map((src, index) => (
                    <img
                      key={src}
                      className={index === activeImage ? 'od-active' : ''}
                      onClick={() => showThumbnail(index)}
                      src={src}
                      alt={`Thumbnail ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="od-info-panel">

            {benefit?.category && <span className="od-category-badge">{benefit.category}</span>}

            {benefit?.name && (
              <div className="od-title-block">
                <h2>{benefit.name}</h2>
              </div>
            )}

            {hasOfferBox && (
              <div className="od-offer-box">
                <div className="od-offer-label">Member Exclusive Offer</div>
                {benefit?.shortDescription && <div className="od-offer-value">{benefit.shortDescription}</div>}

                {isOffline
                  ? benefit?.offerText && (
                      <div className="od-code-row">
                        <div className="od-code-chip od-offer-text">{benefit.offerText}</div>
                      </div>
                    )
                  : benefit?.offerCode && (
                      <div className="od-code-row">
                        <div className="od-code-chip">{benefit.offerCode}</div>
                        <button className={`od-copy-btn ${copied ? 'od-copied' : ''}`} onClick={handleCopyCode}>
                          <i className={copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'}></i>
                        </button>
                      </div>
                    )}
              </div>
            )}

            {hasContactInfo && (
              <>
                <div className="od-claim-heading">Claim this offer at</div>

                <div className="od-contact-list">

                  {showWebsite && (
                    <div className="od-contact-row">
                      <div className="od-icon-circle"><i className="fa-solid fa-globe"></i></div>
                      <div>
                        <span className="od-label">Website</span>
                        <span className="od-value"><a href={partner.website} target="_blank" rel="noopener noreferrer">{partner.website}</a></span>
                      </div>
                    </div>
                  )}

                  {addresses.map((address, index) => (
                    <div className="od-contact-row" key={`${address}-${index}`}>
                      <div className="od-icon-circle"><i className="fa-solid fa-location-dot"></i></div>
                      <div>
                        <span className="od-label">Shop Address</span>
                        <span className="od-value">{address}</span>
                      </div>
                    </div>
                  ))}

                  {partner?.phone && (
                    <div className="od-contact-row">
                      <div className="od-icon-circle"><i className="fa-solid fa-phone"></i></div>
                      <div>
                        <span className="od-label">Contact</span>
                        <span className="od-value"><a href={`tel:${partner.phone}`}>{partner.phone}</a></span>
                      </div>
                    </div>
                  )}

                </div>
              </>
            )}

            {showWebsite && (
              <a href={partner.website} target="_blank" rel="noopener noreferrer" className="od-visit-btn">
                VISIT WEBSITE <i className="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            )}

          </div>

        </div>

        {/* ===== ABOUT (detailed description) ===== */}
        {benefit?.description && (
          <>
            <div className="od-section-header">
              <i className="fa-solid fa-bars"></i>
              <h2>ABOUT {benefit?.name?.toUpperCase()}</h2>
              <div className="od-spacer"></div>
            </div>

            <div className="od-about-panel">
              <p>{benefit.description}</p>
            </div>
          </>
        )}

        {/* ===== PROMOTIONAL BANNER(S) ===== */}
        {ads.length > 0 && (
          <>
            <div className="od-section-header">
              <i className="fa-solid fa-bars"></i>
              <h2>GALLERY</h2>
              <div className="od-spacer"></div>
            </div>

            {ads.map((ad, index) => (
              <div className="od-ad-banner" key={`${ad}-${index}`}>
                <img src={ad} alt="Gallery" />
              </div>
            ))}
          </>
        )}

        <footer className="od-footer">© 2026 Taurian. All rights reserved.</footer>

      </div>
    </div>
  );
};

export default OfferDetailsStatic;
